/**
 * WebRTC Connection Diagnostics
 * Identifies and reports connection establishment issues
 */

export interface ConnectionDiagnostic {
  timestamp: number;
  peerId: string;
  issue: string;
  severity: 'info' | 'warning' | 'error';
  details: any;
  suggestedFix?: string;
}

export class ConnectionDiagnostics {
  private diagnostics = new Map<string, ConnectionDiagnostic[]>();
  private connectionStates = new Map<string, RTCPeerConnectionState>();
  private iceStates = new Map<string, RTCIceConnectionState>();
  private signalingStates = new Map<string, RTCSignalingState>();
  private lastOfferTime = new Map<string, number>();
  private lastAnswerTime = new Map<string, number>();
  private candidateCount = new Map<string, number>();

  // P0-I Phase 1S-L3: bound per-peer diagnostic history.  Every state
  // change / ICE candidate / negotiation event creates a ConnectionDiagnostic
  // object that was retained for the lifetime of the session with no
  // eviction, and the `details` field can contain full RTC stats
  // snapshots (see gatherDetailedStats).  On long-running tabs with
  // many reconnect cycles this grows unbounded per peer.  Cap the
  // per-peer ring buffer to the most recent MAX_DIAGNOSTICS_PER_PEER
  // events using FIFO eviction; older events are discarded because they
  // are debug telemetry, not application state (unlike peer presence
  // or message history, which have separate lifecycle management).
  private static readonly MAX_DIAGNOSTICS_PER_PEER = 50;
  
  /**
   * Monitor a peer connection
   */
  monitorConnection(peerId: string, pc: RTCPeerConnection): void {
    console.log(`[DIAGNOSTICS] Starting monitoring for ${peerId}`);
    
    // Track initial state
    this.connectionStates.set(peerId, pc.connectionState);
    this.iceStates.set(peerId, pc.iceConnectionState);
    this.signalingStates.set(peerId, pc.signalingState);
    this.candidateCount.set(peerId, 0);
    
    // Monitor state changes
    pc.addEventListener('connectionstatechange', () => {
      const oldState = this.connectionStates.get(peerId);
      const newState = pc.connectionState;
      this.connectionStates.set(peerId, newState);
      
      this.log(peerId, 'info', 'Connection state change', {
        from: oldState,
        to: newState
      });
      
      if (newState === 'failed') {
        this.diagnoseConnectionFailure(peerId, pc);
      }
    });
    
    pc.addEventListener('iceconnectionstatechange', () => {
      const oldState = this.iceStates.get(peerId);
      const newState = pc.iceConnectionState;
      this.iceStates.set(peerId, newState);
      
      this.log(peerId, 'info', 'ICE connection state change', {
        from: oldState,
        to: newState
      });
      
      if (newState === 'failed') {
        this.diagnoseIceFailure(peerId, pc);
      } else if (newState === 'disconnected') {
        this.diagnoseIceDisconnection(peerId, pc);
      }
    });
    
    pc.addEventListener('signalingstatechange', () => {
      const oldState = this.signalingStates.get(peerId);
      const newState = pc.signalingState;
      this.signalingStates.set(peerId, newState);
      
      this.log(peerId, 'info', 'Signaling state change', {
        from: oldState,
        to: newState
      });
    });
    
    pc.addEventListener('icecandidate', (event) => {
      if (event.candidate) {
        const count = (this.candidateCount.get(peerId) || 0) + 1;
        this.candidateCount.set(peerId, count);
        
        this.log(peerId, 'info', 'ICE candidate gathered', {
          type: event.candidate.type,
          protocol: event.candidate.protocol,
          address: event.candidate.address ? '[redacted]' : null,
          port: event.candidate.port,
          candidateNumber: count
        });
      } else {
        this.log(peerId, 'info', 'ICE gathering complete', {
          totalCandidates: this.candidateCount.get(peerId) || 0
        });
      }
    });
    
    // Monitor negotiation
    pc.addEventListener('negotiationneeded', () => {
      this.log(peerId, 'info', 'Negotiation needed', {
        signalingState: pc.signalingState,
        iceConnectionState: pc.iceConnectionState
      });
    });
  }
  
  /**
   * Record offer creation
   */
  recordOfferCreation(peerId: string): void {
    this.lastOfferTime.set(peerId, Date.now());
    this.log(peerId, 'info', 'Offer created', {});
  }
  
  /**
   * Record answer creation
   */
  recordAnswerCreation(peerId: string): void {
    this.lastAnswerTime.set(peerId, Date.now());
    const offerTime = this.lastOfferTime.get(peerId);
    
    if (offerTime) {
      const responseTime = Date.now() - offerTime;
      this.log(peerId, 'info', 'Answer created', {
        responseTimeMs: responseTime
      });
      
      if (responseTime > 5000) {
        this.log(peerId, 'warning', 'Slow offer/answer exchange', {
          responseTimeMs: responseTime,
          suggestedFix: 'Check network latency and signaling server performance'
        });
      }
    }
  }
  
  /**
   * Diagnose connection failure
   */
  private diagnoseConnectionFailure(peerId: string, pc: RTCPeerConnection): void {
    const stats = {
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      iceGatheringState: pc.iceGatheringState,
      signalingState: pc.signalingState,
      candidateCount: this.candidateCount.get(peerId) || 0
    };
    
    // Check for common issues
    if (stats.candidateCount === 0) {
      this.log(peerId, 'error', 'No ICE candidates gathered', {
        ...stats,
        suggestedFix: 'Check STUN/TURN server configuration and network connectivity'
      });
    } else if (stats.iceConnectionState === 'failed') {
      this.log(peerId, 'error', 'ICE connection failed', {
        ...stats,
        suggestedFix: 'Peers cannot establish direct connection. TURN server may be needed.'
      });
    } else if (stats.signalingState !== 'stable') {
      this.log(peerId, 'error', 'Signaling state not stable', {
        ...stats,
        suggestedFix: 'Check offer/answer exchange sequence and timing'
      });
    }
    
    // Get detailed stats
    this.gatherDetailedStats(peerId, pc);
  }
  
  /**
   * Diagnose ICE failure
   */
  private diagnoseIceFailure(peerId: string, pc: RTCPeerConnection): void {
    this.log(peerId, 'error', 'ICE connection failed', {
      candidateCount: this.candidateCount.get(peerId) || 0,
      suggestedFix: 'Check firewall settings, NAT configuration, and TURN server availability'
    });
    
    // Check if we have relay candidates
    pc.getStats().then(stats => {
      let hasRelay = false;
      stats.forEach(report => {
        if (report.type === 'local-candidate' && report.candidateType === 'relay') {
          hasRelay = true;
        }
      });
      
      if (!hasRelay) {
        this.log(peerId, 'warning', 'No relay candidates available', {
          suggestedFix: 'Configure TURN server for NAT traversal'
        });
      }
    });
  }
  
  /**
   * Diagnose ICE disconnection
   */
  private diagnoseIceDisconnection(peerId: string, pc: RTCPeerConnection): void {
    this.log(peerId, 'warning', 'ICE connection disconnected', {
      suggestedFix: 'Temporary network interruption. Connection may recover automatically.'
    });
    
    // Set a timer to check if it recovers
    setTimeout(() => {
      if (pc.iceConnectionState === 'disconnected') {
        this.log(peerId, 'error', 'ICE connection failed to recover', {
          suggestedFix: 'Network issue persists. May need to restart connection.'
        });
      }
    }, 10000);
  }
  
  /**
   * Gather detailed statistics
   */
  private async gatherDetailedStats(peerId: string, pc: RTCPeerConnection): Promise<void> {
    try {
      const stats = await pc.getStats();
      const summary: any = {
        candidates: { local: 0, remote: 0 },
        dataChannels: { open: 0, closed: 0 },
        bytesReceived: 0,
        bytesSent: 0
      };
      
      stats.forEach(report => {
        if (report.type === 'local-candidate') {
          summary.candidates.local++;
        } else if (report.type === 'remote-candidate') {
          summary.candidates.remote++;
        } else if (report.type === 'data-channel') {
          if (report.state === 'open') {
            summary.dataChannels.open++;
          } else {
            summary.dataChannels.closed++;
          }
        } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          summary.successfulPair = {
            local: report.localCandidateId,
            remote: report.remoteCandidateId,
            bytesReceived: report.bytesReceived,
            bytesSent: report.bytesSent
          };
        }
      });
      
      this.log(peerId, 'info', 'Detailed statistics', summary);
    } catch (error) {
      this.log(peerId, 'error', 'Failed to gather statistics', { error });
    }
  }
  
  /**
   * Log a diagnostic entry
   */
  private log(peerId: string, severity: 'info' | 'warning' | 'error', issue: string, details: any): void {
    const diagnostic: ConnectionDiagnostic = {
      timestamp: Date.now(),
      peerId,
      issue,
      severity,
      details,
      suggestedFix: details.suggestedFix
    };
    
    const peerDiagnostics = this.diagnostics.get(peerId) || [];
    peerDiagnostics.push(diagnostic);
    // P0-I Phase 1S-L3: FIFO-cap retained history to bound per-peer retention.
    // We keep the most recent MAX_DIAGNOSTICS_PER_PEER events so reconnect
    // debugging still has context, but older events (with their potentially
    // large `details` payloads) are discarded to prevent unbounded growth.
    while (peerDiagnostics.length > ConnectionDiagnostics.MAX_DIAGNOSTICS_PER_PEER) {
      peerDiagnostics.shift();
    }
    this.diagnostics.set(peerId, peerDiagnostics);
    
    // Console output with appropriate level
    const prefix = `[DIAGNOSTICS:${peerId.slice(0, 8)}]`;
    if (severity === 'error') {
      console.error(prefix, issue, details);
    } else if (severity === 'warning') {
      console.warn(prefix, issue, details);
    } else {
      console.log(prefix, issue, details);
    }
  }
  
  /**
   * Public wrapper around the internal `log()` for external callers (e.g.
   * `peerStateStore` pushing lifecycle-transition events into the ring
   * buffer). Severity is fixed to `'info'` because lifecycle transitions are
   * informational by design — actual problems flow through the existing
   * `diagnoseConnectionFailure` / `diagnoseIceFailure` paths, which already
   * emit `'error'` / `'warning'`.
   */
  logInfo(peerId: string, issue: string, details: Record<string, unknown> = {}): void {
    this.log(peerId, 'info', issue, details);
  }

  /**
   * Get diagnostics for a peer
   */
  getDiagnostics(peerId: string): ConnectionDiagnostic[] {
    return this.diagnostics.get(peerId) || [];
  }
  
  /**
   * Get a summary report
   */
  getSummary(peerId: string): string {
    const diagnostics = this.getDiagnostics(peerId);
    const errors = diagnostics.filter(d => d.severity === 'error');
    const warnings = diagnostics.filter(d => d.severity === 'warning');
    
    let summary = `Connection Diagnostics for ${peerId.slice(0, 8)}...\n`;
    summary += `Total Events: ${diagnostics.length}\n`;
    summary += `Errors: ${errors.length}, Warnings: ${warnings.length}\n\n`;
    
    if (errors.length > 0) {
      summary += 'ERRORS:\n';
      errors.forEach(e => {
        summary += `- ${e.issue}\n`;
        if (e.suggestedFix) {
          summary += `  Fix: ${e.suggestedFix}\n`;
        }
      });
      summary += '\n';
    }
    
    if (warnings.length > 0) {
      summary += 'WARNINGS:\n';
      warnings.forEach(w => {
        summary += `- ${w.issue}\n`;
        if (w.suggestedFix) {
          summary += `  Fix: ${w.suggestedFix}\n`;
        }
      });
    }
    
    return summary;
  }
  
  /**
   * Clear diagnostics for a peer
   */
  clearDiagnostics(peerId: string): void {
    this.diagnostics.delete(peerId);
    this.connectionStates.delete(peerId);
    this.iceStates.delete(peerId);
    this.signalingStates.delete(peerId);
    this.lastOfferTime.delete(peerId);
    this.lastAnswerTime.delete(peerId);
    this.candidateCount.delete(peerId);
  }
}

// Singleton instance
export const connectionDiagnostics = new ConnectionDiagnostics();

/**
 * Helper to display diagnostics in console
 */
export function showDiagnostics(peerId: string): void {
  console.log(connectionDiagnostics.getSummary(peerId));
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).connectionDiagnostics = connectionDiagnostics;
  (window as any).showDiagnostics = showDiagnostics;
}
